<template>
  <component
    :is="computedComponent"
    v-bind="computedProps"
    :class="linkClasses"
    class="ds-link"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'
import { COLORS, TYPOGRAPHY } from '@/shared/tokens'

export interface DsLinkProps {
  /**
   * Destino del enlace (ruta de Vue Router o URL externa)
   */
  to?: RouteLocationRaw | string
  /**
   * URL externa (si no se usa to)
   */
  href?: string
  /**
   * Target del enlace (para enlaces externos)
   * @default '_self'
   */
  target?: '_blank' | '_self' | '_parent' | '_top'
  /**
   * Variante del enlace
   * @default 'default'
   */
  dsVariant?: 'default' | 'subtle' | 'bold' | 'underline'
  /**
   * Color del enlace
   * @default 'primary'
   */
  dsColor?: 'primary' | 'accent' | 'success' | 'error' | 'warning' | 'info' | 'gray'
  /**
   * Si el enlace está deshabilitado
   * @default false
   */
  disabled?: boolean
}

const props = withDefaults(defineProps<DsLinkProps>(), {
  target: '_self',
  dsVariant: 'default',
  dsColor: 'primary',
  disabled: false
})

const computedComponent = computed(() => {
  if (props.disabled) return 'span'
  if (props.to) return RouterLink
  return 'a'
})

const computedProps = computed(() => {
  if (props.disabled) return {}

  if (props.to) {
    return { to: props.to }
  }

  return {
    href: props.href,
    target: props.target,
    rel: props.target === '_blank' ? 'noopener noreferrer' : undefined
  }
})

const linkClasses = computed(() => ({
  [`ds-link--${props.dsVariant}`]: true,
  [`ds-link--${props.dsColor}`]: true,
  'ds-link--disabled': props.disabled
}))

const colorValue = computed(() => {
  switch (props.dsColor) {
    case 'accent':
      return COLORS.accent[500]
    case 'success':
      return COLORS.success[500]
    case 'error':
      return COLORS.error[500]
    case 'warning':
      return COLORS.warning[500]
    case 'info':
      return COLORS.info[500]
    case 'gray':
      return COLORS.gray[600]
    default:
      return COLORS.primary[500]
  }
})

const colorHoverValue = computed(() => {
  switch (props.dsColor) {
    case 'accent':
      return COLORS.accent[600]
    case 'success':
      return COLORS.success[600]
    case 'error':
      return COLORS.error[600]
    case 'warning':
      return COLORS.warning[600]
    case 'info':
      return COLORS.info[600]
    case 'gray':
      return COLORS.gray[700]
    default:
      return COLORS.primary[600]
  }
})

// Valores adicionales para v-bind en CSS
const colorGray600 = COLORS.gray[600]
const colorGray400 = COLORS.gray[400]
const fontWeightMedium = TYPOGRAPHY.fontWeight.medium
</script>

<style scoped lang="scss">
.ds-link {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem; /* 4px */
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;

  // Variantes
  &--default {
    color: v-bind(colorValue);
    text-decoration: none;

    &:hover:not(.ds-link--disabled) {
      color: v-bind(colorHoverValue);
      text-decoration: underline;
    }

    &:focus-visible {
      outline: 2px solid v-bind(colorValue);
      outline-offset: 2px;
      border-radius: 0.25rem; /* 4px */
    }
  }

  &--subtle {
    color: v-bind(colorGray600);
    text-decoration: none;

    &:hover:not(.ds-link--disabled) {
      color: v-bind(colorValue);
    }
  }

  &--bold {
    color: v-bind(colorValue);
    font-weight: v-bind(fontWeightMedium);
    text-decoration: none;

    &:hover:not(.ds-link--disabled) {
      color: v-bind(colorHoverValue);
    }
  }

  &--underline {
    color: v-bind(colorValue);
    text-decoration: underline;

    &:hover:not(.ds-link--disabled) {
      color: v-bind(colorHoverValue);
      text-decoration: none;
    }
  }

  // Estado deshabilitado
  &--disabled {
    color: v-bind(colorGray400);
    cursor: not-allowed;
    pointer-events: none;
  }
}
</style>
